import { useEffect } from "react";
import { useReveal } from "@/hooks/useReveal";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  GraduationCap,
  Users,
  Briefcase,
  Building2,
  Gift,
  Sparkles,
} from "lucide-react";
import { useState, FormEvent } from "react";
import { toast } from "@/hooks/use-toast";
import empfehlungHero from "@/assets/empfehlung-hero.webp";
import rewardInvestment50 from "@/assets/reward-investment-50.webp";
import rewardAmazon75 from "@/assets/reward-amazon-75.webp";
import rewardInvestment100 from "@/assets/reward-investment-100.webp";
import rewardAmazon150 from "@/assets/reward-amazon-150.webp";
import rewardIndividual from "@/assets/reward-individual.webp";

const steps = [
  {
    n: "1",
    title: "Empfehlung aussprechen",
    text:
      "Fülle das Empfehlungsformular aus und teile uns mit, wen Du FINABLE empfehlen möchtest.",
  },
  {
    n: "2",
    title: "Wir melden uns",
    text:
      "Wir nehmen unverbindlich Kontakt zu Deiner Empfehlung auf und vereinbaren ein kostenloses Erstgespräch.",
  },
  {
    n: "3",
    title: "Dankeschön erhalten",
    text:
      "Sobald Deine Empfehlung Kunde wird, erhältst Du Dein persönliches Dankeschön aus unserem Prämienkatalog.",
  },
];

const rewards = [
  {
    n: 1,
    title: "50 € Investment-Gutschein",
    desc:
      "Schon für Deine erste Empfehlung erhältst Du einen 50 € Gutschein für Dein Investment-Depot – der perfekte Boost für Deinen Vermögensaufbau.",
    image: rewardInvestment50,
  },
  {
    n: 2,
    title: "75 € Amazon-Gutschein",
    desc:
      "Bei Deiner zweiten Empfehlung schenken wir Dir einen 75 € Amazon-Gutschein – für all das, was Dir Freude macht.",
    image: rewardAmazon75,
  },
  {
    n: 3,
    title: "100 € Investment-Gutschein",
    desc:
      "Mit Deiner dritten Empfehlung legen wir 100 € on top – direkt in Dein Investment-Depot, damit Dein Geld weiter für Dich arbeitet.",
    image: rewardInvestment100,
  },
  {
    n: 4,
    title: "150 € Amazon-Gutschein",
    desc:
      "Für Deine vierte Empfehlung wartet ein 150 € Amazon-Gutschein – ein echtes Dankeschön für Dein Vertrauen.",
    image: rewardAmazon150,
  },
  {
    n: 5,
    title: "Individuell nach Absprache",
    desc:
      "Ab der fünften Empfehlung wird es exklusiv: Wir gestalten Deine Prämie ganz individuell – passend zu Dir und Deinen Wünschen.",
    image: rewardIndividual,
  },
];

const audiences = [
  {
    icon: GraduationCap,
    title: "Ausgelernte Fachkräfte & Hochschulabsolventen",
    text:
      "Fachkräfte und Hochschulabsolventen sollten besonders beim Start im Berufsleben die Weichen richtig stellen. Hierbei unterstützen wir.",
  },
  {
    icon: Users,
    title: "Junge Familien",
    text:
      "Zum Beginn der Familiengründung stehen viele Veränderungen an, bei denen wir helfen können.",
  },
  {
    icon: Briefcase,
    title: "Führungskräfte & Unternehmer",
    text:
      "Personen in Führungspositionen und Unternehmer haben oft individuelle Hürden, bei denen wir unterstützen.",
  },
  {
    icon: Building2,
    title: "Unternehmen",
    text:
      "Von kleinen Einzelunternehmen bis hin zu mittelständischen Kapitalgesellschaften bieten wir individuelle Betreuung an – starte gerne mit Deiner Firma.",
  },
];

const faqs = [
  {
    q: 'Was zählt als "Empfehlung"?',
    a:
      "Sobald sich jemand über Deine Empfehlung bei uns meldet und in mindestens zwei Sparten bei uns betreut wird, zählt die Empfehlung.",
  },
  {
    q: "Wann erhalte ich meine Prämie?",
    a: "Wenn eine Empfehlung von Dir Kunde wird, erhältst Du umgehend Deine Prämie.",
  },
  {
    q: "Was ist, wenn ich gleichzeitig mit einem anderen dieselbe Person empfehle?",
    a:
      "In diesem Fall bekommt immer die Person die Empfehlung zugerechnet, die uns die Person zuletzt empfohlen hat, da das mit großer Wahrscheinlichkeit der ausschlaggebende Grund war, warum die Person zum Beratungstermin erscheint.",
  },
  {
    q: "Kann ich euch ein ganzes Unternehmen empfehlen?",
    a:
      "Wenn Du uns ein Unternehmen empfiehlst und dieses bei uns Firmenkunde (z.B. im Bereich bAV oder bKV) wird, besprechen wir die Prämienanzahl individuell – je nach Größe der Firma – mit Dir.",
  },
];

export default function Empfehlung() {
  useReveal();

  const [form, setForm] = useState({
    referrerName: "",
    refereeName: "",
    refereeEmail: "",
    refereePhone: "",
    message: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const subject = `Neue Empfehlung von ${form.referrerName}`;
    const body = `Empfehler:
Name: ${form.referrerName}

Empfohlene Person:
Name: ${form.refereeName}
E-Mail: ${form.refereeEmail}
Telefon: ${form.refereePhone}

Nachricht:
${form.message}`;
    window.location.href = `mailto:moin@finable.de?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
    toast({
      title: "Fast geschafft!",
      description:
        "Dein E-Mail-Programm öffnet sich – bitte sende die Nachricht ab. Vielen Dank für Deine Empfehlung!",
    });
  };

  // FAQ JSON-LD
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-faq-jsonld", "true");
    script.text = JSON.stringify(faqJsonLd);
    document.head.appendChild(script);
    return () => {
      script.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToForm = () => {
    document.getElementById("empfehlungs-formular")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main>
        {/* Hero */}
        <section className="pt-32 md:pt-40 pb-20 md:pb-28 bg-gradient-hero text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-radial pointer-events-none" />
          <div className="absolute -top-32 right-0 w-[40rem] h-[40rem] rounded-full bg-accent/15 blur-3xl pointer-events-none animate-aurora" />
          <div className="container relative">
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <div className="text-center lg:text-left">
                <div className="reveal">
                  <span className="sr-only">Empfehlungsprogramm</span>
                </div>
                <h1 className="mt-6 font-display text-4xl md:text-5xl lg:text-6xl font-extrabold text-balance reveal">
                  Empfiehl FINABLE <span className="text-accent">weiter</span> – und werde belohnt
                </h1>
                <p className="mt-6 text-lg md:text-xl text-white/80 reveal">
                  Du bist begeistert von unserer Zusammenarbeit? Dann machen wir es Dir jetzt
                  maximal leicht, uns weiterzuempfehlen.
                </p>
                <div className="mt-10 flex justify-center lg:justify-start reveal">
                  <Button variant="hero" size="xl" onClick={scrollToForm}>
                    Jetzt empfehlen <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>
              </div>
              <div className="reveal relative">
                <div className="absolute -inset-4 bg-accent/20 blur-3xl rounded-full pointer-events-none" />
                <div className="relative rounded-3xl overflow-hidden shadow-elegant border border-white/10">
                  <img
                    src={empfehlungHero}
                    alt="Person bei einer Online-Beratung mit FINABLE"
                    width={1920}
                    height={1280}
                    className="w-full h-auto object-cover aspect-[4/3]"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Steps */}
        <section className="py-20 md:py-28">
          <div className="container">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-balance reveal">
                So kannst Du uns empfehlen und gleichzeitig <span className="text-accent">profitieren</span>
              </h2>
              <p className="mt-4 text-muted-foreground reveal">Und so geht's:</p>
            </div>
            <div className="mt-14 grid md:grid-cols-3 gap-6">
              {steps.map((s) => (
                <div
                  key={s.n}
                  className="reveal relative rounded-2xl border border-border bg-card p-8 shadow-card hover:shadow-elegant transition-all"
                >
                  <div className="w-14 h-14 rounded-xl bg-gradient-accent text-accent-foreground font-display font-extrabold text-2xl flex items-center justify-center shadow-glow">
                    {s.n}
                  </div>
                  <h3 className="mt-6 font-display text-xl font-bold">{s.title}</h3>
                  <p className="mt-3 text-muted-foreground leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Win-Win-Win */}
        <section className="py-20 md:py-28 bg-secondary/40">
          <div className="container max-w-5xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="reveal">
                <h2 className="font-display text-3xl md:text-4xl font-extrabold text-balance">
                  Die <span className="text-accent">Win-Win-Win</span> Situation
                </h2>
                <p className="mt-6 text-muted-foreground leading-relaxed">
                  Empfiehlst Du uns weiter, profitieren alle! Deine Empfehlung erhält die Chance auf
                  ein unverbindliches, informatives Gespräch und wird es Dir danken. Wirst Du durch
                  Deine Empfehlung Kunde, wartet ein persönliches Dankeschön auf Dich. Und wir
                  freuen uns über Dein Vertrauen sowie Deine Wertschätzung.
                </p>
                <p className="mt-4 font-semibold">Das ist unser Win-Win-Win-Prinzip!</p>
              </div>
              <ul className="space-y-4 reveal">
                {[
                  "In wenigen Sekunden erledigt",
                  "Hoher Mehrwert für Deine Freunde und Kollegen",
                  "Spürbare Wertschätzung durch den Prämienkatalog",
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-start gap-4 rounded-xl bg-card p-5 shadow-card"
                  >
                    <div className="mt-0.5 w-8 h-8 rounded-lg bg-accent/15 text-accent flex items-center justify-center flex-shrink-0">
                      <Sparkles className="w-4 h-4" />
                    </div>
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Rewards - Zigzag */}
        <section className="py-20 md:py-28">
          <div className="container max-w-5xl">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-balance reveal">
                Empfehlungen machen glücklich:{" "}
                <span className="text-accent">hol Dir Deine Prämien!</span>
              </h2>
              <p className="mt-4 text-muted-foreground reveal">
                Deine Empfehlung ist uns wichtig, und wir möchten Dir persönlich danken! Für jede
                erfolgreiche Weiterempfehlung wartet eine exklusive Prämie auf Dich.
              </p>
            </div>

            <div className="mt-16 space-y-12 md:space-y-20">
              {rewards.map((r, i) => {
                const isReverse = i % 2 === 1;
                return (
                  <div
                    key={r.n}
                    className={`reveal grid md:grid-cols-2 gap-8 md:gap-12 items-center ${
                      isReverse ? "md:[&>*:first-child]:order-2" : ""
                    }`}
                  >
                    <div className="relative rounded-3xl overflow-hidden shadow-elegant border border-border bg-secondary/40">
                      <div className="relative aspect-[4/3] w-full">
                        <img
                          src={r.image}
                          alt={r.title}
                          loading="lazy"
                          width={1024}
                          height={1024}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute top-4 left-4 inline-flex items-center gap-2 rounded-full bg-background/90 backdrop-blur px-3 py-1 text-xs font-semibold text-accent shadow-card">
                          <Gift className="w-3.5 h-3.5" /> {r.n}. Empfehlung
                        </div>
                      </div>
                    </div>
                    <div>
                      <div className="inline-flex items-center gap-2 text-sm font-semibold text-accent">
                        <Gift className="w-4 h-4" /> {r.n}. Empfehlung
                      </div>
                      <h3 className="mt-3 font-display text-2xl md:text-3xl font-bold text-balance">
                        {r.title}
                      </h3>
                      <p className="mt-4 text-muted-foreground leading-relaxed">{r.desc}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-16 flex justify-center reveal">
              <Button variant="hero" size="xl" onClick={scrollToForm}>
                Jetzt empfehlen <ArrowRight className="w-5 h-5" />
              </Button>
            </div>
          </div>
        </section>

        {/* Audiences */}
        <section className="py-20 md:py-28 bg-secondary/40">
          <div className="container">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-balance reveal">
                Diesen Zielgruppen können wir den{" "}
                <span className="text-accent">größten Mehrwert</span> bieten
              </h2>
            </div>
            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {audiences.map((a) => {
                const Icon = a.icon;
                return (
                  <div
                    key={a.title}
                    className="reveal rounded-2xl border border-border bg-card p-6 shadow-card hover:shadow-elegant transition-all"
                  >
                    <div className="w-12 h-12 rounded-xl bg-accent/15 text-accent flex items-center justify-center">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="mt-5 font-display text-lg font-bold">{a.title}</h3>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Contact form */}
        <section id="empfehlungs-formular" className="py-20 md:py-28">
          <div className="container max-w-3xl">
            <div className="text-center max-w-2xl mx-auto">
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-balance reveal">
                Jetzt eine <span className="text-accent">Empfehlung</span> aussprechen
              </h2>
              <p className="mt-4 text-muted-foreground reveal">
                Fülle das Formular aus – wir kümmern uns um den Rest und melden uns bei Deiner
                Empfehlung.
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              className="mt-12 rounded-3xl border border-border bg-card p-6 md:p-10 shadow-elegant reveal space-y-8"
            >
              <div>
                <h3 className="font-display text-xl font-bold mb-5">Deine Angaben (Empfehler)</h3>
                <div className="space-y-2">
                  <Label htmlFor="referrerName">Dein Name *</Label>
                  <Input
                    id="referrerName"
                    name="referrerName"
                    required
                    value={form.referrerName}
                    onChange={handleChange}
                    maxLength={100}
                  />
                </div>
              </div>

              <div className="border-t border-border pt-8">
                <h3 className="font-display text-xl font-bold mb-5">Angaben zur empfohlenen Person</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="refereeName">Name *</Label>
                    <Input
                      id="refereeName"
                      name="refereeName"
                      required
                      value={form.refereeName}
                      onChange={handleChange}
                      maxLength={100}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="refereeEmail">E-Mail *</Label>
                    <Input
                      id="refereeEmail"
                      name="refereeEmail"
                      type="email"
                      required
                      value={form.refereeEmail}
                      onChange={handleChange}
                      maxLength={255}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="refereePhone">Telefonnummer *</Label>
                    <Input
                      id="refereePhone"
                      name="refereePhone"
                      type="tel"
                      required
                      value={form.refereePhone}
                      onChange={handleChange}
                      maxLength={30}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="message">Nachricht (optional)</Label>
                <Textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={handleChange}
                  maxLength={1000}
                  placeholder="Gibt es etwas, das wir wissen sollten?"
                />
              </div>

              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <p className="text-xs text-muted-foreground">
                  Mit Absenden willigst Du ein, dass wir die Empfehlung kontaktieren dürfen.
                </p>
                <Button type="submit" variant="hero" size="lg">
                  Empfehlung senden <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            </form>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 md:py-28 bg-secondary/40">
          <div className="container max-w-3xl">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <h2 className="font-display text-3xl md:text-4xl font-extrabold text-balance reveal">
                Diese Fragen bekommen wir <span className="text-accent">häufig gestellt</span>
              </h2>
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto reveal">
                Alles Wichtige rund um unser Empfehlungsprogramm – verständlich erklärt.
              </p>
            </div>
            <Accordion type="single" collapsible className="space-y-3 reveal">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`item-${i}`}
                  className="rounded-xl border border-border bg-card px-5 shadow-card data-[state=open]:shadow-elegant transition-all"
                >
                  <AccordionTrigger className="text-left font-display text-lg font-semibold text-primary hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        
      </main>
      <Footer />
    </div>
  );
}
